import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { WorkflowGraphStore } from '../../../store/workflow-graph.store';
import { WorkflowEditorService } from '../../../store/workflow-editor.service';
import { WorkflowGraphEdge } from '../../../models/flow-graph.model';
import { WORKFLOW_NODE_TYPE_META, branchKeyDescription } from '../../../models/node-catalog';
import { ToastService } from '../../../../../../../shared/services/toast.service';

/**
 * Inspector de una conexión del workflow.
 *
 * NO hay selector de `branchKey`: lo fija el PUERTO de salida del que nació la conexión
 * (gotcha §6). Aquí solo se edita la etiqueta descriptiva y se puede eliminar la conexión.
 * Reactividad SOLO por signals del store. OnPush.
 */
@Component({
  selector: 'app-wf-edge-config',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (edge(); as e) {
      <div class="space-y-4">
        <div class="rounded-lg bg-gray-50 p-3 dark:bg-white/5">
          <p class="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">Conexión</p>
          <p class="mt-1 text-sm text-gray-700 dark:text-gray-200">
            {{ sourceName() }} <span class="text-gray-400">→</span> {{ targetName() }}
          </p>
          <p class="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <span class="h-1.5 w-1.5 rounded-full bg-brand-500"></span>
            Sale por:
            <span class="rounded-full bg-gray-100 px-2 py-0.5 font-mono text-[11px] text-gray-700 dark:bg-gray-700 dark:text-gray-200">
              {{ e.branchKey }}
            </span>
          </p>
          <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">{{ branchDescription() }}</p>
        </div>

        <div>
          <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">Etiqueta</label>
          <input type="text"
                 class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                 [value]="e.label" [disabled]="readOnly()" placeholder="Opcional"
                 (input)="updateLabel(inputValue($event))" />
          <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Solo para leer el diagrama; no afecta al motor.</p>
        </div>

        <div class="rounded-lg bg-gray-50 p-2.5 text-[11px] text-gray-500 dark:bg-white/5 dark:text-gray-400">
          La rama la decide el puerto del que arrastraste. Para cambiarla, conecta desde otro puerto del paso de origen.
        </div>

        <button type="button"
                class="w-full rounded-lg border border-error-200 px-3 py-2 text-xs font-medium text-error-500 transition hover:bg-error-50 disabled:opacity-40 dark:border-error-500/30 dark:hover:bg-error-500/10"
                [disabled]="readOnly()" (click)="remove()">
          Eliminar conexión
        </button>
      </div>
    }
  `
})
export class WorkflowEdgeConfigComponent {
  private readonly store = inject(WorkflowGraphStore);
  private readonly editor = inject(WorkflowEditorService);
  private readonly toast = inject(ToastService);
  private readonly meta = WORKFLOW_NODE_TYPE_META;

  readonly edgeId = input.required<string>();

  protected readonly readOnly = computed(() => this.editor.readOnly());

  protected readonly edge = computed<WorkflowGraphEdge | undefined>(() => {
    // Se lee la lista para refrescar cuando el store cambia.
    this.store.edges();
    return this.store.findEdge(this.edgeId());
  });

  protected readonly sourceName = computed(() => this.nodeName(this.edge()?.sourceNodeKey));
  protected readonly targetName = computed(() => this.nodeName(this.edge()?.targetNodeKey));
  protected readonly branchDescription = computed(() => {
    const key = this.edge()?.branchKey;
    return key ? branchKeyDescription(key) : '';
  });

  private nodeName(nodeKey?: string): string {
    if (!nodeKey) {
      return '';
    }
    const node = this.store.findNode(nodeKey);
    return node ? node.title.trim() || this.meta[node.nodeType].label : nodeKey;
  }

  protected inputValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  protected updateLabel(label: string): void {
    if (this.readOnly()) {
      return;
    }
    this.store.updateEdge(this.edgeId(), { label });
    this.editor.queueAutosave();
  }

  protected remove(): void {
    if (this.readOnly()) {
      return;
    }
    this.store.removeEdge(this.edgeId());
    this.editor.queueAutosave();
    this.toast.success({ title: 'Conexión eliminada', message: 'Se eliminó la conexión correctamente.' });
  }
}
